import React from 'react'
import styled from 'styled-components'
import { mediaQueryUp } from 'components/utils/breakpoint'
import useCruiseOfferView from 'hooks/Cruise/useCruiseOfferView'
import { rem } from 'polished'
import Group from 'components/utils/Group'
import CruiseTileImageCarousel from './Common/CruiseTileImageCarousel'
import CruiseTileHeader from './Common/CruiseTileHeader'
import CruiseTileDetails from './Common/CruiseTileDetails'
import CruiseTilePrices from './Common/CruiseTilePrices'
import TextButton from 'components/Luxkit/Button/TextButton'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import OfferTilePricing from 'components/OfferList/OfferListTiles/Support/OfferTilePricing'
import ResponsiveImage from 'components/Common/ResponsiveImage'
import CruiseTileSearchPrices from './Common/CruiseTileSearchPrices'
import { useLocation } from 'react-router'
import TripGuard from 'tripPlanner/components/TripGuard/TripGuard'
import FloatingBookmarkContainer from 'tripPlanner/components/Bookmark/Common/FloatingBookmarkContainer'
import BookmarkButton from 'tripPlanner/components/Bookmark/BookmarkButton'
import Divider from 'components/Luxkit/Divider'
import ProductPaletteProvider from 'contexts/ProductPaletteContext'

const CruiseTileWrapper = styled.div`
  background-color: ${(props) => props.theme.palette.neutral.eight};
  border-radius: ${(props) => props.theme.borderRadius.M};
  border: 1px solid ${({ theme }) => theme.palette.neutral.five};
`
const CruiseTileContentWrapper = styled.div`
  grid-template-columns: 1fr;
  grid-template-rows: ${rem(220)} 1fr;
  position: relative;
  display: grid;
  min-height: ${rem(350)};

  ${mediaQueryUp.tablet} {
    grid-template-columns: ${rem(280)} 1fr;
    grid-template-rows: 1fr;
  }
  ${mediaQueryUp.desktop} {
    grid-template-columns: ${rem(500)} 1fr;
    grid-template-rows: 1fr;
  }

  &.small-image{
    ${mediaQueryUp.tablet} {
      grid-template-columns: ${rem(150)} 1fr;
      grid-template-rows: 1fr;
    }
    ${mediaQueryUp.desktop} {
      grid-template-columns: ${rem(280)} 1fr;
      grid-template-rows: 1fr;
    }
  }
`

const CruiseTileContent = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${rem(16)};
  ${mediaQueryUp.desktop} {
    grid-template-columns: 1fr ${rem(200)};
  }
  padding: ${rem(20)} ${rem(24)};
`

interface Props {
  offer: App.CruiseOffer | App.Offer | App.OfferSummary;
  tileType?: 'standalone' | 'search';
}

function CruiseTile({ offer, tileType }: Props) {
  const {
    offerTitle,
    cruiseLineName,
    cruiseLineImage,
    offerDescription,
    departureCity,
    destinationCity,
    cities,
    offerImages,
    isSpecialOffer,
    lowestPrice,
    shipDetailsLabel,
    tileInclusions,
    itineraryDetailsLabel,
    productType,
    isVelocityEnabled,
    promotionDetails,
    evergreenInclusions,
    earliestDepartureDate,
    laterDates,
    disableDeposits,
    cabinCategory,
    discountPills,
    isFlashOffer,
    flashOffer,
    palette,
  } = useCruiseOfferView({ offer })
  const isStandalone = tileType === 'standalone'
  const location = useLocation()
  const useSmallImage = location.pathname.includes('search')
  const isSearch = tileType === 'search'
  return (
    <CruiseTileWrapper>
      <CruiseTileContentWrapper className={useSmallImage ? 'small-image' : ''}>
        <ProductPaletteProvider palette={palette}>
          <CruiseTileImageCarousel
            images={offerImages}
            isVelocityEnabled={isVelocityEnabled}
            productType={productType}
          />

          <CruiseTileContent>
            <Group gap={12} direction="vertical">
              <CruiseTileHeader
                isSpecialOffer={isSpecialOffer}
                offerTitle={offerTitle}
                departureCity={departureCity}
                destinationCity={destinationCity}
                promotionDetails={promotionDetails}
                cruiseLineImage={cruiseLineImage}
                cruiseLineName={cruiseLineName}
              />

              <CruiseTileDetails
                isFlashOffer={isFlashOffer}
                shipDetailsLabel={shipDetailsLabel}
                itineraryDetailsLabel={itineraryDetailsLabel}
                offerDescription={offerDescription}
                promotionDetails={promotionDetails}
                evergreenInclusions={evergreenInclusions}
                tileInclusions={tileInclusions}
                cities={cities}
                earliestDepartureDate={earliestDepartureDate}
                laterDates={laterDates}
              />
            </Group>
            <Group
              direction="vertical"
              tabletHorizontalAlign="center"
              tabletVerticalAlign="end"
              fullHeight
              fullWidth
            >
              <Group gap={4} fullHeight direction="horizontal" horizontalAlign="end" verticalAlign="center">
                <CSSBreakpoint min="desktop">
                  {cruiseLineImage && (
                    <ResponsiveImage
                      id={cruiseLineImage}
                      src={cruiseLineImage}
                      width={150}
                      quality="eco"
                      alt={cruiseLineName}
                      role="presentation"
                    />
                  )}
                </CSSBreakpoint>
              </Group>

              <Group
                fullWidth
                gap={16}
                direction="horizontal"
                tabletDirection="vertical"
                horizontalAlign="space-between"
                tabletHorizontalAlign="start"
                desktopHorizontalAlign="stretch"
                verticalAlign="end"
              >
                {!flashOffer && (
                  <CruiseTilePrices
                    isFlashOffer={isFlashOffer}
                    promotionDetails={promotionDetails}
                    disableDeposits={disableDeposits}
                    cabinCategory={cabinCategory}
                    lowestPrice={lowestPrice}
                    discountPills={discountPills}
                    isStandalone={isStandalone}
                  />
                )}

                {!!flashOffer && !flashOffer.bundledWithFlightsOnly && flashOffer.lowestPricePackage && (
                  <OfferTilePricing
                    offer={flashOffer}
                    pkg={flashOffer.lowestPricePackage}
                    offerUrl=""
                  />
                )}

                {(isStandalone || flashOffer) && <>
                  <CSSBreakpoint max="mobile">
                    <TextButton kind="primary" nonInteractable>View</TextButton>
                  </CSSBreakpoint>
                  <CSSBreakpoint min="tablet">
                    <TextButton fit="desktop-full-width" kind="primary" nonInteractable>View offer</TextButton>
                  </CSSBreakpoint>
                </>}

                {!isStandalone && !flashOffer &&
                  <CSSBreakpoint min="desktop" as="div">
                    <TextButton fit="desktop-full-width" kind="primary" nonInteractable>
                      View offer
                    </TextButton>
                  </CSSBreakpoint>
                }

                <TripGuard>
                  <FloatingBookmarkContainer>
                    <BookmarkButton offer={offer as App.Offer} />
                  </FloatingBookmarkContainer>
                </TripGuard>
              </Group>
            </Group>
          </CruiseTileContent>
        </ProductPaletteProvider>
      </CruiseTileContentWrapper>
      {!isFlashOffer && isSearch && (
        <>
          <Divider
            direction="horizontal"
            kind="primary"
          />
          <CruiseTileSearchPrices
            offer={offer as App.CruiseOffer}
            discountPills={discountPills}
            promotionDetails={promotionDetails}
          />
        </>
      )}
    </CruiseTileWrapper>
  )
}

export default CruiseTile
